'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

type TorrentHit = {
  title: string;
  infoHash: string;
  size: number;
  seeders: number;
  leechers: number;
  publishDate: string;
};

const fmtSize = (n: number) => {
  if (!n) return '?';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(1)} ${u[i]}`;
};

const MAX_RETRIES = 12; // ~48s of retrying before giving up

export default function TorrentPlayer({
  seriesTitle,
  episodeNumber,
  cacheKey,
}: {
  seriesTitle: string;
  episodeNumber: number;
  cacheKey: string;
}) {
  const [results, setResults] = useState<TorrentHit[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchError, setSearchError] = useState('');
  const [active, setActive] = useState<TorrentHit | null>(null);
  const [streamUrl, setStreamUrl] = useState('');
  const [starting, setStarting] = useState(false); // waiting for first bytes
  const [playError, setPlayError] = useState('');
  const [retries, setRetries] = useState(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const rank = useCallback(
    (list: TorrentHit[]): TorrentHit[] => {
      const epRe = new RegExp(
        `(?:ep(?:isode)?|#)?\\s*0*${episodeNumber}(?:[^0-9]|$)`,
        'i'
      );
      const mp4 = (t: string) => /\.(mp4|m4v|webm)\b/i.test(t);
      const score = (h: TorrentHit) => {
        let s = h.seeders * 100;
        if (epRe.test(h.title)) s += 5000;
        if (mp4(h.title)) s += 2000;
        if (/(dub|hindi|spanish|french|arabic|turkish)/i.test(h.title)) s -= 3000;
        return s;
      };
      return [...list].sort((a, b) => score(b) - score(a));
    },
    [episodeNumber]
  );

  // MKV/AVI/MOV can't play in Chrome natively → use the ffmpeg remux endpoint
  const streamUrlFor = useCallback((h: TorrentHit) => {
    const remux = /\.(mkv|avi|mov)\b/i.test(h.title) ? '?remux=1' : '';
    return `/api/stream/${h.infoHash}/0${remux}`;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setSearchError('');
      try {
        const q = encodeURIComponent(`${seriesTitle} ${episodeNumber}`);
        const res = await fetch(`/api/torrents/search?q=${q}&key=${encodeURIComponent(cacheKey)}`);
        const data = await res.json();
        if (cancelled) return;
        if (!data.results?.length) {
          setSearchError('No torrents found for this episode on Nyaa.');
          setLoading(false);
          return;
        }
        const ranked = rank(data.results);
        setResults(ranked);
        const best = ranked[0];
        if (best) {
          setActive(best);
          setStarting(true);
          setStreamUrl(streamUrlFor(best));
        }
        setLoading(false);
      } catch (e: any) {
        if (!cancelled) {
          setSearchError('Torrent search failed: ' + (e?.message || ''));
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (retryTimer.current) clearTimeout(retryTimer.current);
    };
  }, [cacheKey, seriesTitle, episodeNumber, rank, streamUrlFor]);

  const pick = (h: TorrentHit) => {
    if (retryTimer.current) clearTimeout(retryTimer.current);
    setActive(h);
    setPlayError('');
    setRetries(0);
    setStarting(true);
    setStreamUrl(streamUrlFor(h));
  };

  const retry = () => {
    if (!active) return;
    setPlayError('');
    setStarting(true);
    // force the <video> to remount + refetch
    setStreamUrl('');
    setTimeout(() => setStreamUrl(streamUrlFor(active)), 150);
  };

  const onVideoError = () => {
    setStarting(false);
    if (retries < MAX_RETRIES) {
      // first pieces may still be downloading — retry with backoff
      const delay = 3000 + retries * 700;
      if (retryTimer.current) clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(() => {
        setRetries((r) => r + 1);
        retry();
      }, delay);
    } else {
      setPlayError(
        "Couldn't play this source — the torrent may have no active seeders. Try another torrent (an MP4 if available), or the embed player tab below."
      );
    }
  };

  const onPlaying = () => setStarting(false);

  return (
    <div className="w-full">
      {/* player area */}
      <div className="w-full bg-black rounded-xl overflow-hidden border border-zinc-800">
        <div className="relative w-full" style={{ aspectRatio: '16/9' }}>
          {streamUrl ? (
            <>
              <video
                ref={videoRef}
                key={streamUrl + '-' + retries}
                src={streamUrl}
                controls
                autoPlay
                className="w-full h-full"
                onError={onVideoError}
                onPlaying={onPlaying}
                onWaiting={() => setStarting(true)}
              />
              {starting && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-400 gap-3 bg-black/60">
                  <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm">
                    {retries > 0
                      ? `Connecting to peers… (attempt ${retries + 1})`
                      : 'Starting stream…'}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-400 gap-3 p-6 text-center">
              {loading ? (
                <>
                  <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm">Searching Nyaa for episode {episodeNumber}…</p>
                </>
              ) : (
                <>
                  <p className="text-lg font-semibold">Pick a torrent source</p>
                  <p className="text-sm max-w-md">
                    Ad-free direct streaming. Choose a release below.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {playError && (
        <div className="mt-2 flex flex-wrap items-center gap-3 rounded-lg border border-red-600/40 bg-red-600/10 p-3 text-sm text-red-200">
          <span>{playError}</span>
          {active && (
            <button
              onClick={() => {
                setRetries(0);
                retry();
              }}
              className="px-3 py-1 rounded-md bg-red-600/30 hover:bg-red-600/50 text-red-100"
            >
              Retry
            </button>
          )}
        </div>
      )}
      {searchError && <p className="mt-2 text-sm text-red-400">{searchError}</p>}

      {/* results */}
      {results.length > 0 && (
        <div className="mt-3">
          <p className="text-sm font-semibold text-zinc-400 mb-2">
            Torrent sources (Nyaa) — MP4s ranked first, best seeders on top
          </p>
          <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto pr-1">
            {results.map((h, i) => (
              <button
                key={h.infoHash + i}
                onClick={() => pick(h)}
                className={`text-left px-3 py-2 rounded-lg border text-sm transition flex items-center gap-3 ${
                  active?.infoHash === h.infoHash
                    ? 'bg-violet-600/15 border-violet-500 text-violet-200'
                    : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-violet-500'
                }`}
              >
                <span className="w-12 shrink-0 text-xs font-bold text-emerald-400">
                  {h.seeders} ▲
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block truncate">{h.title}</span>
                  <span className="text-xs text-zinc-500">
                    {fmtSize(h.size)}
                    {h.publishDate ? ` · ${h.publishDate.slice(0, 10)}` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-xs text-zinc-500">
                  {active?.infoHash === h.infoHash ? '▶' : ''}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
